import { API_BASE_URL } from '@synergy/config';
import {
    ApplicationLogLevel,
    ApplicationLogResult,
    ApplicationLogSearchFilters,
    SettingsApi,
} from '@synergy/admin/app-settings/data-access';
import {
    Box,
    Collapse,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { ChangeEvent, FormEvent, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { PoolButton, PoolTypography } from '@synergy/components-next';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const HANDLERS = ['nested', 'mail', 'xml', 'ftp', 'search_engine'] as const;
type HandlerName = (typeof HANDLERS)[number];

const LEVELS: ApplicationLogLevel[] = ['DEBUG', 'INFO', 'NOTICE', 'WARNING', 'ERROR', 'CRITICAL', 'ALERT', 'EMERGENCY'];

const api = new SettingsApi(API_BASE_URL);

type LogRowProps = {
    entry: ApplicationLogResult;
};

const LogRow = ({ entry }: LogRowProps) => {
    const [open, setOpen] = useState(false);

    const parsed = useMemo(() => {
        try {
            return JSON.parse(entry.message) as Record<string, unknown>;
        } catch {
            return null;
        }
    }, [entry.message]);

    const level = (parsed?.['level_name'] as string | undefined) ?? '—';
    const channel = (parsed?.['channel'] as string | undefined) ?? '—';
    const message = (parsed?.['message'] as string | undefined) ?? entry.message;
    const timestamp = entry.timestamp ?? (parsed?.['datetime'] as string | undefined) ?? '';

    return (
        <>
            <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell padding="checkbox">
                    <IconButton size="small" onClick={() => setOpen(o => !o)} aria-label="toggle row">
                        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{timestamp}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{level}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{channel}</TableCell>
                <TableCell
                    sx={{
                        maxWidth: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                    title={message}
                >
                    {message}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell
                    colSpan={5}
                    sx={{ paddingTop: 0, paddingBottom: 0, borderBottom: open ? undefined : 'unset' }}
                >
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box
                            component="pre"
                            sx={{
                                fontFamily: 'monospace',
                                fontSize: 12,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                                margin: 0,
                                paddingY: 1.5,
                            }}
                        >
                            {parsed ? JSON.stringify(parsed, null, 2) : entry.message}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

export const SystemLogs = () => {
    const baseUrl = API_BASE_URL;
    const [handler, setHandler] = useState<HandlerName>('nested');
    const [query, setQuery] = useState('');
    const [level, setLevel] = useState<ApplicationLogLevel | ''>('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [limit, setLimit] = useState(200);
    const [results, setResults] = useState<ApplicationLogResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [executed, setExecuted] = useState(false);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoading(true);
        setError(null);
        const filters: ApplicationLogSearchFilters = {
            query: query.trim() || null,
            level: level || null,
            from: from ? new Date(from).toISOString() : null,
            to: to ? new Date(to).toISOString() : null,
            limit,
            offset: 0,
        };
        const response = await api.searchApplicationLogs(handler, filters);
        if (response.success) {
            setResults(response.result ?? []);
        } else {
            setError('Suche fehlgeschlagen.');
            setResults([]);
        }
        setExecuted(true);
        setLoading(false);
    };

    return (
        <Stack gap={3} sx={{ width: '100%' }}>
            <PoolTypography variant="body1" translationId="app.settings.system.logs" sx={{ fontWeight: 600 }} />

            <Stack flexDirection="row" gap={2} alignItems="center" flexWrap="wrap">
                <a href={`${baseUrl}/settings/application-logs/nested`} target="_blank" rel="noreferrer">
                    <PoolButton variant="outlined">
                        <FormattedMessage id="app.settings.system.logs.applicationLogs" />
                    </PoolButton>
                </a>
                <a href={`${baseUrl}/settings/application-logs/mail`} target="_blank" rel="noreferrer">
                    <PoolButton variant="outlined">
                        <FormattedMessage id="app.settings.system.logs.mailLogs" />
                    </PoolButton>
                </a>
                <a href={`${baseUrl}/settings/application-logs/xml`} target="_blank" rel="noreferrer">
                    <PoolButton variant="outlined">
                        <FormattedMessage id="app.settings.system.logs.xmlLogs" />
                    </PoolButton>
                </a>
                <a href={`${baseUrl}/settings/application-logs/ftp`} target="_blank" rel="noreferrer">
                    <PoolButton variant="outlined">
                        <FormattedMessage id="app.settings.system.logs.ftpLogs" />
                    </PoolButton>
                </a>
                <a href={`${baseUrl}/settings/failed-queue`} target="_blank" rel="noreferrer">
                    <PoolButton variant="outlined">
                        <FormattedMessage id="app.settings.system.logs.failedQueue" />
                    </PoolButton>
                </a>
            </Stack>

            <Box component="form" onSubmit={handleSubmit}>
                <Stack direction="row" gap={2} flexWrap="wrap" alignItems="flex-end">
                    <TextField
                        label="Handler"
                        select
                        size="small"
                        value={handler}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setHandler(e.target.value as HandlerName)}
                        sx={{ minWidth: 160 }}
                    >
                        {HANDLERS.map(h => (
                            <MenuItem key={h} value={h}>
                                {h}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Level"
                        select
                        size="small"
                        value={level}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setLevel(e.target.value as ApplicationLogLevel | '')
                        }
                        sx={{ minWidth: 140 }}
                    >
                        <MenuItem value="">(alle)</MenuItem>
                        {LEVELS.map(l => (
                            <MenuItem key={l} value={l}>
                                {l}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Von"
                        type="datetime-local"
                        size="small"
                        value={from}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 200 }}
                    />
                    <TextField
                        label="Bis"
                        type="datetime-local"
                        size="small"
                        value={to}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 200 }}
                    />
                    <TextField
                        label="Suche"
                        size="small"
                        value={query}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                        placeholder="Volltext"
                        sx={{ minWidth: 240 }}
                    />
                    <TextField
                        label="Limit"
                        type="number"
                        size="small"
                        value={limit}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setLimit(Math.max(1, Math.min(1000, parseInt(e.target.value, 10) || 200)))
                        }
                        sx={{ width: 100 }}
                        inputProps={{ min: 1, max: 1000 }}
                    />
                    <PoolButton type="submit" disabled={loading}>
                        {loading ? 'Suche…' : 'Suchen'}
                    </PoolButton>
                </Stack>
            </Box>

            {error && (
                <Typography color="error" variant="body2">
                    {error}
                </Typography>
            )}

            {executed && !loading && results.length === 0 && !error && (
                <Typography variant="body2" color="text.secondary">
                    Keine Treffer.
                </Typography>
            )}

            {results.length > 0 && (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" />
                                <TableCell>Zeit</TableCell>
                                <TableCell>Level</TableCell>
                                <TableCell>Channel</TableCell>
                                <TableCell>Message</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {results.map((entry, idx) => (
                                <LogRow key={`${entry.timestamp ?? 'null'}-${idx}`} entry={entry} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Stack>
    );
};
